import React, { useState, useEffect, useRef } from 'react';
import { complianceService, documentService } from '../services/api';
import {
  Clock, Upload, CheckCircle2, XCircle, Circle, AlertTriangle, FileText,
} from 'lucide-react';

const ComplianceChecklist = () => {
  const [checklist, setChecklist] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [expirationDate, setExpirationDate] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [checklistData, docsData] = await Promise.all([
        complianceService.getChecklist(),
        documentService.getMyDocuments()
      ]);
      setChecklist(checklistData);
      setDocuments(docsData);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, JPG, or PNG file.');
      return;
    }

    if (file.size > maxSize) {
      alert('File size must be less than 10MB.');
      return;
    }

    setUploadFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedItem) return;

    setUploading(true);
    try {
      await documentService.uploadDocument(
        uploadFile,
        selectedItem.item_type,
        expirationDate || null
      );
      
      await fetchData();
      
      setSelectedItem(null);
      setUploadFile(null);
      setExpirationDate('');
      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: 'gray', bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock, iconCls: 'text-gray-500', label: 'Pending' },
      submitted: { color: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Upload, iconCls: 'text-yellow-600', label: 'Submitted' },
      approved: { color: 'green', bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2, iconCls: 'text-green-600', label: 'Approved' },
      rejected: { color: 'red', bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, iconCls: 'text-red-600', label: 'Rejected' },
      expired: { color: 'red', bg: 'bg-red-100', text: 'text-red-800', icon: Circle, iconCls: 'text-red-600 fill-red-600', label: 'Expired' },
    };
    return configs[status] || configs.pending;
  };

  const formatItemName = (itemType) => {
    const names = {
      cdl_front: 'CDL - Front',
      cdl_back: 'CDL - Back',
      medical_certificate: 'Medical Certificate',
      social_security_card: 'Social Security Card',
      proof_of_address: 'Proof of Address',
      w9_form: 'W-9 Form',
      insurance_certificate: 'Certificate of Insurance',
      equipment_photos: 'Equipment Photos',
      mvr: 'Motor Vehicle Record',
      drug_test: 'Drug Test Results',
    };
    return names[itemType] || itemType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getExpirationWarning = (item) => {
    if (!item.expiration_date) return null;
    
    const expDate = new Date(item.expiration_date);
    const today = new Date();
    const daysUntil = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      return { message: 'Expired', color: 'text-red-600', urgent: true };
    } else if (daysUntil <= 7) {
      return { message: `Expires in ${daysUntil} days`, color: 'text-red-600', urgent: true };
    } else if (daysUntil <= 14) {
      return { message: `Expires in ${daysUntil} days`, color: 'text-orange-600', urgent: true };
    } else if (daysUntil <= 30) {
      return { message: `Expires in ${daysUntil} days`, color: 'text-yellow-600', urgent: false };
    }
    return null;
  };

  const needsUpload = (status) => ['pending', 'rejected', 'expired'].includes(status);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading compliance checklist...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="compliance-checklist">
      {/* Header Card */}
      <div className="bg-white shadow-sm rounded-lg p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">DOT Compliance Checklist</h2>
            <p className="text-sm text-gray-600 mt-1">Complete all items to become an active driver.</p>
          </div>
          
          {/* Progress Ring - Mobile Friendly */}
          {checklist && (
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke="#e5e7eb"
                    strokeWidth="8%"
                    fill="none"
                  />
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke="#2563eb"
                    strokeWidth="8%"
                    fill="none"
                    strokeDasharray={`${(checklist.completed_items / checklist.total_items) * 283} 283`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm sm:text-base font-bold text-gray-900">
                    {Math.round((checklist.completed_items / checklist.total_items) * 100)}%
                  </span>
                </div>
              </div>
              <div className="text-sm">
                <p className="font-semibold text-gray-900">
                  {checklist.completed_items} of {checklist.total_items}
                </p>
                <p className="text-gray-500">items approved</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Checklist Items */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="divide-y divide-gray-100">
          {checklist?.checklist.map((item) => {
            const statusConfig = getStatusConfig(item.status);
            const expirationWarning = getExpirationWarning(item);
            
            return (
              <div
                key={item.id}
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  expirationWarning?.urgent ? 'bg-red-50/50' : ''
                }`}
                data-testid={`compliance-item-${item.item_type}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Status Icon & Name */}
                  <div className="flex items-center gap-3 flex-1">
                    {(() => { const StatusIcon = statusConfig.icon; return <StatusIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${statusConfig.iconCls}`} />; })()}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                        {formatItemName(item.item_type)}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                          {statusConfig.label}
                        </span>
                        {expirationWarning && (
                          <span className={`text-xs ${expirationWarning.color} font-medium inline-flex items-center gap-1`}>
                            <AlertTriangle className="w-3.5 h-3.5" /> {expirationWarning.message}
                          </span>
                        )}
                        {item.expiration_date && !expirationWarning && (
                          <span className="text-xs text-gray-500">
                            Expires: {new Date(item.expiration_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Upload Button */}
                  {needsUpload(item.status) && (
                    <button
                      onClick={() => setSelectedItem(item)}
                      className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors flex items-center justify-center gap-2"
                      data-testid={`upload-btn-${item.item_type}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload Modal - Full Screen on Mobile */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
          onClick={(e) => e.target === e.currentTarget && setSelectedItem(null)}
          data-testid="upload-modal"
        >
          <div className="bg-white w-full sm:rounded-lg sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-lg">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Upload Document</h3>
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setUploadFile(null);
                  setExpirationDate('');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-4 sm:px-6 py-4">
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Document Type:</strong> {formatItemName(selectedItem.item_type)}
                </p>
              </div>

              <form onSubmit={handleUpload} className="space-y-4">
                {/* Drag & Drop Zone */}
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition-colors ${
                    dragActive 
                      ? 'border-blue-500 bg-blue-50' 
                      : uploadFile 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="file-input"
                  />
                  
                  {uploadFile ? (
                    <div className="space-y-2">
                      <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto" />
                      <p className="text-sm font-medium text-green-800">{uploadFile.name}</p>
                      <p className="text-xs text-green-600">
                        {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadFile(null);
                        }}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FileText className="w-10 h-10 text-gray-400 mx-auto" />
                      <p className="text-sm font-medium text-gray-700">
                        Tap to select or drag & drop
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF, JPG, or PNG (max 10MB)
                      </p>
                    </div>
                  )}
                </div>

                {/* Expiration Date (for certain documents) */}
                {['cdl_front', 'cdl_back', 'medical_certificate', 'insurance_certificate'].includes(selectedItem.item_type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiration Date
                    </label>
                    <input
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      data-testid="expiration-date-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Required for compliance tracking
                    </p>
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={!uploadFile || uploading}
                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    data-testid="upload-submit-btn"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Upload Document
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedItem(null);
                      setUploadFile(null);
                      setExpirationDate('');
                    }}
                    className="flex-1 sm:flex-none bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                    data-testid="upload-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceChecklist;
